import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crosshair, Building2, ChevronDown, ChevronRight, FileText, Calendar, CheckCircle, UserPlus, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PositionTracker() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drilldown, setDrilldown] = useState(null); // {positionId, positionTitle, type: 'resumes'|'interviews'|'closures'|'joinings'}
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/position-tracker`, { headers: getHeaders() })
      .then(r => {
        setData(r.data);
        // Auto-expand all clients
        const expanded = {};
        r.data.forEach(c => { expanded[c.client_name] = true; });
        setExpandedClients(expanded);
      })
      .catch(() => toast.error('Failed to load position tracker'))
      .finally(() => setLoading(false));
  }, []);

  const toggleClient = (name) => {
    setExpandedClients(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleDrilldown = async (positionId, positionTitle, type) => {
    setDrilldown({ positionId, positionTitle, type });
    setDrilldownLoading(true);
    try {
      if (type === 'resumes') {
        // Resumes have position_id directly
        const res = await axios.get(`${API}/${type}`, { headers: getHeaders() });
        setDrilldownData(res.data.filter(item => item.position_id === positionId));
      } else {
        // Interviews, closures, joinings link via resume_id
        // First get resumes for this position to get resume_ids
        const resumeRes = await axios.get(`${API}/resumes`, { headers: getHeaders() });
        const posResumeIds = new Set(resumeRes.data.filter(r => r.position_id === positionId).map(r => r.id));

        if (type === 'interviews') {
          const res = await axios.get(`${API}/interviews`, { headers: getHeaders() });
          setDrilldownData(res.data.filter(item => posResumeIds.has(item.resume_id)));
        } else if (type === 'closures') {
          const res = await axios.get(`${API}/closures`, { headers: getHeaders() });
          setDrilldownData(res.data.filter(item => posResumeIds.has(item.resume_id)));
        } else if (type === 'joinings') {
          // Joinings link via closure_id → closure.resume_id
          const closureRes = await axios.get(`${API}/closures`, { headers: getHeaders() });
          const posClosure = closureRes.data.filter(c => posResumeIds.has(c.resume_id));
          const posClosureIds = new Set(posClosure.map(c => c.id));
          const res = await axios.get(`${API}/joinings`, { headers: getHeaders() });
          setDrilldownData(res.data.filter(item => posClosureIds.has(item.closure_id)));
        }
      }
    } catch { toast.error(`Failed to load ${type}`); }
    finally { setDrilldownLoading(false); }
  };

  // Filter logic
  const filteredData = data.map(client => {
    let positions = client.positions;
    if (search) {
      const s = search.toLowerCase();
      positions = positions.filter(p =>
        p.title.toLowerCase().includes(s) ||
        (p.assigned_to || []).join(' ').toLowerCase().includes(s) ||
        (p.location || '').toLowerCase().includes(s)
      );
    }
    if (statusFilter !== 'all') {
      positions = positions.filter(p => p.status === statusFilter);
    }
    if (positions.length === 0) return null;
    return { ...client, positions };
  }).filter(Boolean);

  const totalPositions = filteredData.reduce((acc, c) => acc + c.positions.length, 0);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
  );

  return (
    <div data-testid="position-tracker-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Crosshair className="inline-block mr-2 text-blue-600" size={28} />Position Tracker
        </h1>
        <p className="text-slate-500 text-sm">Client-wise position breakdown with pipeline metrics</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'CLIENTS', value: filteredData.length, color: 'bg-slate-900 text-white' },
          { label: 'RESUMES', value: filteredData.reduce((a, c) => a + c.totals.resumes, 0), color: 'bg-blue-600 text-white' },
          { label: 'INTERVIEWS', value: filteredData.reduce((a, c) => a + c.totals.interviews, 0), color: 'bg-rose-600 text-white' },
          { label: 'CLOSURES', value: filteredData.reduce((a, c) => a + c.totals.closures, 0), color: 'bg-emerald-600 text-white' },
          { label: 'JOININGS', value: filteredData.reduce((a, c) => a + c.totals.joinings, 0), color: 'bg-violet-600 text-white' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-blue-100 rounded-xl p-4 text-center shadow-sm" data-testid={`stat-${s.label.toLowerCase()}`}>
            <p className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
            <Badge className={`${s.color} text-[10px] font-bold mt-1.5`}>{s.label}</Badge>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded-lg shadow-sm p-4 mb-5 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase text-blue-900">Search</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search position, recruiter, location..." className="h-9 pl-9 rounded border-blue-200 text-sm" data-testid="tracker-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-blue-900">Status</Label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm" data-testid="tracker-status-filter">
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="hold">On Hold</option>
          </select>
        </div>
        <p className="text-xs text-slate-500 self-end pb-2">{totalPositions} positions across {filteredData.length} clients</p>
      </div>

      {/* Client-wise breakdown */}
      <div className="space-y-4" data-testid="client-list">
        {filteredData.map(client => (
          <div key={client.client_name} className="bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden" data-testid={`client-${client.client_name}`}>
            {/* Client Header */}
            <button onClick={() => toggleClient(client.client_name)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-blue-50 to-white hover:from-blue-100 transition-colors text-left">
              <div className="flex items-center gap-3">
                {expandedClients[client.client_name] ? <ChevronDown size={18} className="text-blue-500" /> : <ChevronRight size={18} className="text-blue-500" />}
                <Building2 size={18} className="text-blue-600" />
                <h2 className="text-base font-extrabold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{client.client_name}</h2>
                <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600">{client.positions.length} positions</Badge>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="text-slate-500"><FileText size={12} className="inline mr-1" />{client.totals.resumes}</span>
                <span className="text-slate-500"><Calendar size={12} className="inline mr-1" />{client.totals.interviews}</span>
                <span className="text-slate-500"><CheckCircle size={12} className="inline mr-1" />{client.totals.closures}</span>
                <span className="text-slate-500"><UserPlus size={12} className="inline mr-1" />{client.totals.joinings}</span>
              </div>
            </button>

            {/* Position Table */}
            {expandedClients[client.client_name] && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="font-bold text-blue-900 text-xs">Position</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs">Status</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs">Assigned To</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Resumes</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Interviews</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Closures</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Joinings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {client.positions.map(pos => (
                    <TableRow key={pos.id} className="hover:bg-blue-50/50" data-testid={`position-row-${pos.id}`}>
                      <TableCell>
                        <p className="text-sm font-semibold text-slate-800">{pos.title}</p>
                        {pos.location && <p className="text-[10px] text-slate-400">{pos.location}</p>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] font-bold px-2 ${
                          pos.status === 'open' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          pos.status === 'closed' ? 'bg-slate-200 text-slate-600 border-slate-300' :
                          'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>{pos.status?.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{(pos.assigned_to || []).join(', ') || '—'}</TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => handleDrilldown(pos.id, pos.title, 'resumes')}
                          className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          data-testid={`drill-resumes-${pos.id}`}>{pos.resumes}</button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => handleDrilldown(pos.id, pos.title, 'interviews')}
                          className="text-sm font-bold text-rose-600 hover:text-rose-800 hover:underline cursor-pointer"
                          data-testid={`drill-interviews-${pos.id}`}>{pos.interviews}</button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => handleDrilldown(pos.id, pos.title, 'closures')}
                          className="text-sm font-bold text-emerald-600 hover:text-emerald-800 hover:underline cursor-pointer"
                          data-testid={`drill-closures-${pos.id}`}>{pos.closures}</button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button onClick={() => handleDrilldown(pos.id, pos.title, 'joinings')}
                          className="text-sm font-bold text-violet-600 hover:text-violet-800 hover:underline cursor-pointer"
                          data-testid={`drill-joinings-${pos.id}`}>{pos.joinings}</button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-blue-50/70 border-t-2 border-blue-200">
                    <TableCell className="text-xs font-extrabold text-blue-800">TOTAL ({client.positions.length} POSITIONS)</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-center text-sm font-extrabold text-blue-600">{client.totals.resumes}</TableCell>
                    <TableCell className="text-center text-sm font-extrabold text-rose-600">{client.totals.interviews}</TableCell>
                    <TableCell className="text-center text-sm font-extrabold text-emerald-600">{client.totals.closures}</TableCell>
                    <TableCell className="text-center text-sm font-extrabold text-violet-600">{client.totals.joinings}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        ))}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 bg-white border border-blue-100 rounded-xl">
          <Crosshair size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No positions found matching your filters</p>
        </div>
      )}

      {/* Drilldown Dialog */}
      <Dialog open={!!drilldown} onOpenChange={() => setDrilldown(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900">
              {drilldown?.positionTitle} — {drilldown?.type?.charAt(0).toUpperCase()}{drilldown?.type?.slice(1)}
            </DialogTitle>
          </DialogHeader>
          {drilldownLoading ? (
            <div className="text-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" /></div>
          ) : drilldownData.length === 0 ? (
            <p className="text-center py-8 text-sm text-slate-400">No {drilldown?.type} found for this position</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {drilldown?.type === 'resumes' && (<>
                    <TableHead className="font-bold text-xs">Candidate</TableHead>
                    <TableHead className="font-bold text-xs">Email</TableHead>
                    <TableHead className="font-bold text-xs">Phone</TableHead>
                    <TableHead className="font-bold text-xs">Status</TableHead>
                    <TableHead className="font-bold text-xs">Submitted</TableHead>
                  </>)}
                  {drilldown?.type === 'interviews' && (<>
                    <TableHead className="font-bold text-xs">Candidate</TableHead>
                    <TableHead className="font-bold text-xs">Date</TableHead>
                    <TableHead className="font-bold text-xs">Type</TableHead>
                    <TableHead className="font-bold text-xs">Status</TableHead>
                  </>)}
                  {drilldown?.type === 'closures' && (<>
                    <TableHead className="font-bold text-xs">Candidate</TableHead>
                    <TableHead className="font-bold text-xs">Revenue</TableHead>
                    <TableHead className="font-bold text-xs">Date</TableHead>
                  </>)}
                  {drilldown?.type === 'joinings' && (<>
                    <TableHead className="font-bold text-xs">Candidate</TableHead>
                    <TableHead className="font-bold text-xs">Join Date</TableHead>
                    <TableHead className="font-bold text-xs">Status</TableHead>
                  </>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {drilldownData.map(item => (
                  <TableRow key={item.id}>
                    {drilldown?.type === 'resumes' && (<>
                      <TableCell className="text-sm font-medium">{item.candidate_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{item.email}</TableCell>
                      <TableCell className="text-xs text-slate-500">{item.phone}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{item.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</TableCell>
                    </>)}
                    {drilldown?.type === 'interviews' && (<>
                      <TableCell className="text-sm font-medium">{item.candidate_name}</TableCell>
                      <TableCell className="text-xs">{item.date ? new Date(item.date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell className="text-xs">{item.interview_type || item.type || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{item.status}</Badge></TableCell>
                    </>)}
                    {drilldown?.type === 'closures' && (<>
                      <TableCell className="text-sm font-medium">{item.candidate_name}</TableCell>
                      <TableCell className="text-xs font-bold text-emerald-600">{item.revenue ? `\u20B9${item.revenue.toLocaleString()}` : '-'}</TableCell>
                      <TableCell className="text-xs">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</TableCell>
                    </>)}
                    {drilldown?.type === 'joinings' && (<>
                      <TableCell className="text-sm font-medium">{item.candidate_name}</TableCell>
                      <TableCell className="text-xs">{item.joining_date ? new Date(item.joining_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{item.status}</Badge></TableCell>
                    </>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
